<template>
  <div class="animated fadeIn">
    <b-row class="justify-content-center">
      <b-col md="12">
        <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
          <i class="fal fa-spinner fa-spin" /> Loading
        </div>
        <div v-else>
          <basic-heading :title="`New Schedule: ${selected_date}`">
            <template #right-content>
              <header-button-group :button_config="button_config" />
            </template>
          </basic-heading>
          <schedule-form
            :sample_types="sample_type_collection"
            :context_prep_processes="context_prep_processes"
            :context_test_processes="context_test_processes"
          />
          <div class="animated fadeIn">
            <b-row class="justify-content-center">
              <b-col>
                <b-button
                  v-if="!submitting"
                  @click="submitForm"
                  class="my-4 float-right py-3 px-5"
                  variant="success"
                >
                  <i class="fal fa-save" /> Save
                </b-button>
                <b-button
                  v-if="submitting"
                  class="my-4 float-right py-3 px-5"
                  variant="muted"
                  disabled="disabled"
                >
                  <i class="fal fa-spinner fa-spin" /> Saving...
                </b-button>
              </b-col>
            </b-row>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>
<script>
import PrepProcessCollection from "@/collections/lab/PrepProcessCollection";
import TestProcessCollection from "@/collections/lab/TestProcessCollection";
import BasicHeading from "@/components/shared/BasicHeading";
import HeaderButtonGroup from "@/components/shared/headerbuttons/HeaderButtonGroup";
import ScheduleForm from "@/components/lab/schedule/form/ScheduleForm";
import ProcessScheduleModel from "@/models/lab/ProcessScheduleModel";
import { mapActions, mapGetters } from "vuex";
import { getCollection } from "@/shared/fetch-utils";
import { LabRepositoryFactory } from "@/repositories/lab/LabRepositoryFactory";

const ScheduleRepository = LabRepositoryFactory.get("schedule");

export default {
  name: "ScheduleAdd",
  components: {
    BasicHeading,
    HeaderButtonGroup,
    ScheduleForm,
  },
  data() {
    return {
      loading: true,
      submitting: false,
      laboratory_id: process.env.VUE_APP_DEFAULT_LABORATORY_ID || null,
      sample_type_collection: null,
      prep_process_collection: null,
      test_process_collection: null,
      context__prep_processes: new PrepProcessCollection(),
      context__test_processes: new TestProcessCollection(),
    };
  },
  computed: {
    ...mapGetters({
      selected_date: "selectedDate",
      schedule_form: "scheduleForm",
    }),
    button_config() {
      return {
        buttons: [
          {
            icon: "fa-calendar",
            title: "Schedule",
            variant: "info",
            linkto: "/schedules",
          },
        ],
      };
    },
    selected_sample_type_id() {
      return this.schedule_form.sample_type_id;
    },
    selected_processes() {
      return this.schedule_form.processes;
    },
    selected_instruments() {
      return this.schedule_form.instruments;
    },
    selected_process_type() {
      return this.schedule_form.process_type;
    },
    has_instruments() {
      return !!this.schedule_form.instruments.models.length;
    },
    context_prep_processes() {
      const prep_collection = new PrepProcessCollection();

      this.prep_process_collection.each((process) => {
        if (process.sample_type_id === this.selected_sample_type_id) {
          prep_collection.add(process);
        }
      });

      return this.selected_sample_type_id ? prep_collection : null;
    },
    context_test_processes() {
      const test_collection = new TestProcessCollection();

      this.test_process_collection.each((process) => {
        if (process.sample_type_id === this.selected_sample_type_id) {
          test_collection.add(process);
        }
      });

      return this.selected_sample_type_id ? test_collection : null;
    },
  },
  methods: {
    ...mapActions(["RESET_SCHEDULE_FORM"]),
    async fetchProcesses(type) {
      const connections = [
        "laboratory",
        "schedulableTasks",
        "schedulableTasks.instrumentInstances.instrument",
        "schedulableTasks.analyses.analyte",
        "sampleType",
      ];
      const filters = [
        {
          logic: "filterEquals",
          field: "laboratory_id",
          value: this.laboratory_id,
        },
      ];

      this[`${type}_collection`] = await getCollection({
        collection_type: type,
        filters,
        connections,
        sort: [
          {
            logic: "sortAddAsc",
            field: "order",
          },
        ],
        limit: "all",
        message: this.$bvToast,
      });
    },
    handleProcessTypeUpdate() {
      this.prep_process_collection.each((process) => {
        this.context__prep_processes.add(process);
      });

      this.test_process_collection.each((process) => {
        this.context__test_processes.add(process);
      });
    },
    async init() {
      this.sample_type_collection = await getCollection({
        collection_type: "sample_type",
        limit: "all",
        message: this.$bvToast,
      });
      await this.fetchProcesses("prep_process");
      await this.fetchProcesses("test_process");

      this.handleProcessTypeUpdate();

      this.loading = false;
    },
    async submitForm() {
      this.submitting = true;

      try {
        const { data } = await ScheduleRepository.create({
          date: this.selected_date,
          laboratory_id: this.laboratory_id,
          sample_type_id: this.selected_sample_type_id,
          process_type: this.selected_process_type
        });

        this.storeScheduleProcesses(data.id);
      } catch ({ response }) {
        this.$bvToast.toast(response ? response.data.error : 'Unable to create the schedule', {
          title: "Add Failed",
          variant: "danger",
        });
      } finally {
        this.submitting = false
      }
    },
    storeScheduleProcesses(schedule_id) {
      const schedulable_process_type =
        this.selected_process_type === "Prep"
          ? "App\\Models\\PrepProcess"
          : "App\\Models\\TestProcess";
      const payload_list = this.selected_processes.map((process) => {
        let process_schedule_instrument_instances = [];

        process.instruments.forEach((instrument) => {
          instrument.tools.each((tool) => {
            process_schedule_instrument_instances = [
              ...process_schedule_instrument_instances,
              {
                instrument_instance_id: tool.id,
                process_task_id: instrument.collection.id,
                process_task_type: schedulable_process_type.replace(
                  "Process",
                  "Task"
                ),
              },
            ];
          });
        });

        return {
          schedulable_process_type,
          schedule_id,
          process_schedule_instrument_instances,
          schedulable_process_id: process.process.id,
        };
      });

      let saves = [];

      payload_list.forEach((payload) => {
        const scheduleProcess = new ProcessScheduleModel(payload);

        saves = [...saves, scheduleProcess.save()];
      });

      Promise.all(saves)
        .then(() => {
          this.$bvToast.toast("Add succeeded", {
            title: "Schedule stored.",
            variant: "success",
          });

          this.RESET_SCHEDULE_FORM();
        })
        .catch(() => {
          this.$bvToast.toast("Schedule could not be stored.", {
            title: "Add Failed",
            variant: "danger",
          });
        });

      this.submitting = false;

      this.$router.push(`/schedules/${schedule_id}/sample-cohort`);
    },
  },
  mounted() {
    this.loading = true;

    this.init();
  },
};
</script>
